import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckBadgeIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import type { Contributor } from '../../types/buildExpansion';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';

const requesterName = (request: Contributor): string =>
  request.name || (request.username ? `@${request.username}` : null) || request.shop_slug || 'Someone';

const formatDateRange = (request: Contributor): string | null => {
  const parts = [request.date_from, request.date_to].filter(Boolean);
  return parts.length > 0 ? parts.join(' to ') : null;
};

/**
 * Pending contributor requests for the signed-in user, with accept/decline.
 * Compact enough to sit on a dashboard. Renders nothing when signed out.
 */
export const ContributorRequestsPanel: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const [requests, setRequests] = useState<Contributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pending = await apiService.getPendingContributorRequests();
      setRequests(pending);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load contributor requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    void load();
  }, [isAuthenticated, load]);

  if (!isAuthenticated) return null;

  const act = async (request: Contributor, action: 'accept' | 'decline') => {
    setActingId(request.id);
    try {
      await apiService.contributorAction(action, { contributor_id: request.id });
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
      showToast(
        action === 'accept'
          ? `Accepted contributor request from ${requesterName(request)}.`
          : `Declined contributor request from ${requesterName(request)}.`,
        'success'
      );
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Could not update the request. Try again.', 'error');
    } finally {
      setActingId(null);
    }
  };

  return (
    <section className={`card p-5 ${className}`.trim()} aria-labelledby="contributor-requests-heading">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 id="contributor-requests-heading" className="heading-card">
          Contributor requests
          {!loading && requests.length > 0 ? (
            <span className="ml-2 badge-brand align-middle">{requests.length}</span>
          ) : null}
        </h2>
      </div>

      <div aria-live="polite" className="sr-only">
        {loading
          ? 'Loading contributor requests…'
          : `${requests.length} pending ${requests.length === 1 ? 'request' : 'requests'}`}
      </div>

      {loading ? (
        <div className="space-y-3" role="status" aria-busy="true">
          <span className="sr-only">Loading contributor requests…</span>
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="h-20 rounded-lg bg-[#141414]/70 animate-pulse motion-reduce:animate-none"
              aria-hidden
            />
          ))}
        </div>
      ) : error ? (
        <div>
          <div role="alert" className="alert-error mb-3">
            {error}
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          variant="dense"
          icon={<UserGroupIcon className="w-10 h-10" />}
          title="No pending requests"
          description="Requests to credit shops or people on builds will show up here."
        />
      ) : (
        <ul className="space-y-3 list-none">
          {requests.map((request) => {
            const dateRange = formatDateRange(request);
            const acting = actingId === request.id;
            return (
              <li key={request.id} className="rounded-lg border border-white/[0.06] bg-[#0c0c0c]/70 p-3.5">
                <div className="flex items-start gap-3">
                  {request.shop_logo ? (
                    <img
                      src={apiService.getFileUrl(request.shop_logo) || request.shop_logo}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover border border-white/[0.08] bg-[#0a0a0a] shrink-0"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-lg bg-[#141414] border border-white/[0.08] flex items-center justify-center text-xs font-semibold text-gray-400 shrink-0"
                      aria-hidden
                    >
                      {requesterName(request).replace(/^@/, '').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white flex items-center gap-1.5 flex-wrap">
                      {request.shop_slug ? (
                        <Link
                          to={`/shops/${request.shop_slug}`}
                          className="hover:text-orange-300 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                        >
                          {requesterName(request)}
                        </Link>
                      ) : (
                        <span className="truncate">{requesterName(request)}</span>
                      )}
                      {request.shop_verified ? (
                        <span className="inline-flex items-center text-orange-300 shrink-0">
                          <CheckBadgeIcon className="w-4 h-4" aria-hidden />
                          <span className="sr-only">Verified shop</span>
                        </span>
                      ) : null}
                      <span className="text-xs text-gray-500 font-normal">as {request.role}</span>
                    </p>
                    {request.work_description ? (
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{request.work_description}</p>
                    ) : null}
                    {dateRange ? <p className="text-[11px] text-gray-500 mt-1">{dateRange}</p> : null}
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={actingId !== null}
                    loading={acting}
                    loadingLabel="Saving…"
                    onClick={() => void act(request, 'accept')}
                  >
                    Accept
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={actingId !== null}
                    onClick={() => void act(request, 'decline')}
                  >
                    Decline
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default ContributorRequestsPanel;
